/**
 * useEmbeds Hook
 * Module 07 - Dashboard UI
 * Section 08 - State Management
 * 
 * Hook for managing embeds data and operations with React Query
 * Enhanced with centralized query keys and optimistic updates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { embedsService } from '../services';
import { useToast } from './useToast';
import { Embed, CreateEmbedInput, UpdateEmbedInput } from '../types';
import { queryKeys } from '../lib/queryClient';

type ApiError = AxiosError<{ message?: string }>;

/**
 * useEmbeds provides embeds data and operations
 * Uses React Query for caching and automatic refetching
 */
export const useEmbeds = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Fetch embeds
  const {
    data: embeds = [],
    isLoading,
    error,
    refetch,
  } = useQuery<Embed[]>({
    queryKey: queryKeys.embeds.all,
    queryFn: () => embedsService.getEmbeds(),
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchInterval: 60000, // Refetch every minute for view count updates
  });

  // Create embed mutation
  const createEmbedMutation = useMutation({
    mutationFn: (input: CreateEmbedInput) => embedsService.createEmbed(input),
    onSuccess: (newEmbed) => {
      // Optimistically add the new embed
      queryClient.setQueryData<Embed[]>(
        queryKeys.embeds.all,
        (old) => [...(old ?? []), newEmbed]
      );
      showToast('success', 'Embed created successfully');
    },
    onError: (error: ApiError) => {
      showToast(
        'error',
        error.response?.data?.message || 'Failed to create embed'
      );
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.embeds.all });
    },
  });

  // Update embed mutation
  const updateEmbedMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEmbedInput }) =>
      embedsService.updateEmbed(id, input),
    onMutate: async ({ id, input }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.embeds.all });

      // Snapshot the previous value
      const previousEmbeds = queryClient.getQueryData<Embed[]>(queryKeys.embeds.all);

      // Optimistically update
      queryClient.setQueryData<Embed[]>(
        queryKeys.embeds.all,
        (old) =>
          old?.map((embed) =>
            embed.id === id
              ? {
                  ...embed,
                  ...input,
                  settings: {
                    ...embed.settings,
                    ...(input.settings ?? {}),
                  },
                  updatedAt: new Date().toISOString(),
                }
              : embed
          ) ?? []
      );

      return { previousEmbeds };
    },
    onError: (error: ApiError, _variables, context) => {
      // Rollback on error
      if (context?.previousEmbeds) {
        queryClient.setQueryData(queryKeys.embeds.all, context.previousEmbeds);
      }
      showToast(
        'error',
        error.response?.data?.message || 'Failed to update embed'
      );
    },
    onSuccess: () => {
      showToast('success', 'Embed updated successfully');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.embeds.all });
    },
  });

  // Delete embed mutation
  const deleteEmbedMutation = useMutation({
    mutationFn: (id: string) => embedsService.deleteEmbed(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.embeds.all });

      // Snapshot the previous value
      const previousEmbeds = queryClient.getQueryData<Embed[]>(queryKeys.embeds.all);

      // Optimistically remove the embed
      queryClient.setQueryData<Embed[]>(
        queryKeys.embeds.all,
        (old) => old?.filter((embed) => embed.id !== id) ?? []
      );

      return { previousEmbeds };
    },
    onError: (error: ApiError, _id, context) => {
      // Rollback on error
      if (context?.previousEmbeds) {
        queryClient.setQueryData(queryKeys.embeds.all, context.previousEmbeds);
      }
      showToast(
        'error',
        error.response?.data?.message || 'Failed to delete embed'
      );
    },
    onSuccess: () => {
      showToast('success', 'Embed deleted successfully');
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.embeds.all });
    },
  });

  // Toggle embed status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      embedsService.toggleEmbedStatus(id, isActive),
    onMutate: async ({ id, isActive }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.embeds.all });

      // Snapshot the previous value
      const previousEmbeds = queryClient.getQueryData<Embed[]>(queryKeys.embeds.all);

      // Optimistically update
      queryClient.setQueryData<Embed[]>(
        queryKeys.embeds.all,
        (old) =>
          old?.map((embed) =>
            embed.id === id
              ? { ...embed, isActive, updatedAt: new Date().toISOString() }
              : embed
          ) ?? []
      );

      return { previousEmbeds };
    },
    onError: (error: ApiError, _variables, context) => {
      // Rollback on error
      if (context?.previousEmbeds) {
        queryClient.setQueryData(queryKeys.embeds.all, context.previousEmbeds);
      }
      showToast(
        'error',
        error.response?.data?.message || 'Failed to update embed status'
      );
    },
    onSuccess: (_, variables) => {
      showToast(
        'success',
        `Embed ${variables.isActive ? 'activated' : 'deactivated'} successfully`
      );
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: queryKeys.embeds.all });
    },
  });

  // Regenerate token mutation
  const regenerateTokenMutation = useMutation({
    mutationFn: (id: string) => embedsService.regenerateToken(id),
    onSuccess: () => {
      showToast('success', 'Embed token regenerated successfully');
    },
    onError: (error: ApiError) => {
      showToast(
        'error',
        error.response?.data?.message || 'Failed to regenerate token'
      );
    },
    onSettled: () => {
      // Refetch to get the new token
      queryClient.invalidateQueries({ queryKey: queryKeys.embeds.all });
    },
  });

  // Reset view count mutation
  const resetViewCountMutation = useMutation({
    mutationFn: (id: string) => embedsService.resetViewCount(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.embeds.all });
      const previousEmbeds = queryClient.getQueryData<Embed[]>(queryKeys.embeds.all);
      queryClient.setQueryData<Embed[]>(
        queryKeys.embeds.all,
        (old) =>
          old?.map((embed) =>
            embed.id === id
              ? { ...embed, viewCount: 0, lastViewedAt: null }
              : embed
          ) ?? []
      );
      return { previousEmbeds };
    },
    onError: (error: ApiError, _id, context) => {
      if (context?.previousEmbeds) {
        queryClient.setQueryData(queryKeys.embeds.all, context.previousEmbeds);
      }
      showToast(
        'error',
        error.response?.data?.message || 'Failed to reset view count'
      );
    },
    onSuccess: () => {
      showToast('success', 'View count reset successfully');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.embeds.all });
    },
  });

  // Get embeds by model
  const getEmbedsByModel = (modelId: string) => {
    return embeds.filter((embed) => embed.modelId === modelId);
  };

  return {
    // Data
    embeds,
    isLoading,
    error,

    // Operations
    createEmbed: createEmbedMutation.mutateAsync,
    updateEmbed: (id: string, input: UpdateEmbedInput) =>
      updateEmbedMutation.mutateAsync({ id, input }),
    deleteEmbed: deleteEmbedMutation.mutateAsync,
    toggleEmbedStatus: (id: string, isActive: boolean) =>
      toggleStatusMutation.mutateAsync({ id, isActive }),
    regenerateToken: regenerateTokenMutation.mutateAsync,
    resetViewCount: resetViewCountMutation.mutateAsync,
    refetch,
    getEmbedsByModel,

    // Loading states
    isCreating: createEmbedMutation.isPending,
    isUpdating: updateEmbedMutation.isPending,
    isDeleting: deleteEmbedMutation.isPending,
    isTogglingStatus: toggleStatusMutation.isPending,
    isRegeneratingToken: regenerateTokenMutation.isPending,
    isResettingViewCount: resetViewCountMutation.isPending,

    // Computed values
    activeEmbeds: embeds.filter((e) => e.isActive),
    inactiveEmbeds: embeds.filter((e) => !e.isActive),
    totalViews: embeds.reduce((acc, e) => acc + (e.viewCount || 0), 0),
  };
};
